// CopyDrawer：两种批量操作模式（顶部 Tab 切换）：
// - "overwrite": 选 1 张源 + N 个已存在目标 + 字段勾选 → 把字段覆盖过去
// - "batch":     不选源，直接填带占位符的模板（map_id / name / JSON 字段）+ 序列
//                → 展开为 N 张新地图（含冲突跳过/覆盖）
//
// 两种模式互不影响表单状态。提交回调由调用方负责实际 PUT。

import { useMemo, useState } from "react";
import { Sheet } from "@/components/ui-lite/Sheet";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Input";
import type {
  GameContinent,
  GameMap,
  GameMapSummary,
  MapUpsertInput,
} from "@/api/games";
import { BatchCreate, type BatchCreateSubmitPayload } from "./BatchCreate";

export type CopyDrawerMode = "overwrite" | "batch";

export interface CopyDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  maps: GameMapSummary[];
  /** 批量新建需要大陆列表填入下拉。 */
  continents?: GameContinent[];
  /** mode="overwrite" 时的回调（把源字段覆盖到已有目标）。 */
  onConfirm: (plan: CopyPlanRequest) => Promise<void> | void;
  /** mode="batch" 时的回调；省略时批量新建 tab 不显示。 */
  onConfirmBatch?: (payload: BatchCreateSubmitPayload) => Promise<void> | void;
  submitting?: boolean;
}

export interface CopyPlan {
  source: GameMap;
  targets: GameMap[];
  fields: CopyableField[];
}

export interface CopyPlanRequest {
  sourceID: string;
  targetIDs: string[];
  fields: CopyableField[];
}

export type CopyableField =
  | "enter"
  | "edges"
  | "match"
  | "fight"
  | "enterConditions"
  | "selectable_default"
  | "allow_transit_when_cooldown"
  | "nested_group";

const FIELD_LABELS: Record<CopyableField, string> = {
  enter: "enter (进入步骤)",
  edges: "edges",
  match: "match",
  fight: "fight_defaults",
  enterConditions: "enterConditions",
  selectable_default: "默认可挂",
  allow_transit_when_cooldown: "冷却期过路",
  nested_group: "nested_group",
};

const ALL_FIELDS: CopyableField[] = Object.keys(FIELD_LABELS) as CopyableField[];
const SUMMARY_FIELDS = new Set<CopyableField>([
  "edges",
  "selectable_default",
  "allow_transit_when_cooldown",
  "nested_group",
]);

export function CopyDrawer({
  open,
  onOpenChange,
  maps,
  continents = [],
  onConfirm,
  onConfirmBatch,
  submitting,
}: CopyDrawerProps) {
  const [mode, setMode] = useState<CopyDrawerMode>("overwrite");
  const [sourceID, setSourceID] = useState<string>("");
  const [targetSet, setTargetSet] = useState<Set<string>>(new Set());
  const [fieldSet, setFieldSet] = useState<Set<CopyableField>>(
    new Set(["enter", "edges"]),
  );

  const source = useMemo(
    () => maps.find((m) => m.map_id === sourceID) ?? null,
    [maps, sourceID],
  );
  const targets = useMemo(
    () => maps.filter((m) => targetSet.has(m.map_id) && m.map_id !== sourceID),
    [maps, targetSet, sourceID],
  );

  function toggleTarget(id: string) {
    const next = new Set(targetSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setTargetSet(next);
  }
  function toggleField(f: CopyableField) {
    const next = new Set(fieldSet);
    if (next.has(f)) next.delete(f);
    else next.add(f);
    setFieldSet(next);
  }

  const canConfirm =
    !!source && targets.length > 0 && fieldSet.size > 0 && !submitting;

  async function handleConfirm() {
    if (!source || !canConfirm) return;
    await onConfirm({
      sourceID: source.map_id,
      targetIDs: targets.map((target) => target.map_id),
      fields: Array.from(fieldSet),
    });
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="批量复制 / 批量新建地图"
      description="覆盖到已有目标：把源字段复制到多个已存在的目标。批量新建：填模板 + 序列，展开为 N 张新地图。"
    >
      {onConfirmBatch ? (
        <div className="mb-3 flex gap-1 rounded border border-[hsl(var(--border))] p-1 text-xs">
          <ModeTab
            active={mode === "overwrite"}
            onClick={() => setMode("overwrite")}
            label="覆盖到已有目标"
          />
          <ModeTab
            active={mode === "batch"}
            onClick={() => setMode("batch")}
            label="批量新建"
          />
        </div>
      ) : null}

      {mode === "batch" && onConfirmBatch ? (
        <BatchCreate
          maps={maps}
          continents={continents}
          submitting={submitting}
          onSubmit={onConfirmBatch}
          onCancel={() => onOpenChange(false)}
        />
      ) : (
      <div className="flex flex-1 flex-col gap-4">
        <Field label="源 map">
          <select
            value={sourceID}
            onChange={(e) => setSourceID(e.target.value)}
            className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
          >
            <option value="">—</option>
            {maps.map((m) => (
              <option key={m.map_id} value={m.map_id}>
                {m.name || m.map_id}（{m.map_id}）
              </option>
            ))}
          </select>
        </Field>

        <div>
          <div className="mb-1 text-xs text-[hsl(var(--muted-foreground))]">
            目标 maps（多选）
          </div>
          <div className="max-h-40 overflow-y-auto rounded border border-[hsl(var(--border))] p-2">
            {maps.map((m) => {
              const disabled = m.map_id === sourceID;
              const checked = targetSet.has(m.map_id);
              return (
                <label
                  key={m.map_id}
                  className="flex items-center gap-2 py-0.5 text-xs"
                  style={{ opacity: disabled ? 0.5 : 1 }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleTarget(m.map_id)}
                  />
                  <span className="font-mono">{m.map_id}</span>
                  <span className="truncate">{m.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-1 text-xs text-[hsl(var(--muted-foreground))]">
            要复制的字段
          </div>
          <div className="grid grid-cols-2 gap-1">
            {ALL_FIELDS.map((f) => (
              <label key={f} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={fieldSet.has(f)}
                  onChange={() => toggleField(f)}
                />
                {FIELD_LABELS[f]}
              </label>
            ))}
          </div>
        </div>

        {source && targets.length > 0 && fieldSet.size > 0 ? (
          <DiffPreview
            source={source}
            targets={targets}
            fields={Array.from(fieldSet)}
          />
        ) : null}

        <div className="mt-auto flex items-center justify-end gap-2 pt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            取消
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting
              ? "复制中…"
              : `复制到 ${targets.length} 个目标`}
          </Button>
        </div>
      </div>
      )}
    </Sheet>
  );
}

function ModeTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex-1 rounded px-2 py-1 transition " +
        (active
          ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
          : "hover:bg-[hsl(var(--muted))]")
      }
    >
      {label}
    </button>
  );
}

function DiffPreview({
  source,
  targets,
  fields,
}: {
  source: GameMapSummary;
  targets: GameMapSummary[];
  fields: CopyableField[];
}) {
  return (
    <div className="rounded border border-[hsl(var(--border))] p-2 text-xs">
      <div className="mb-1 font-semibold">将变更</div>
      <ul className="space-y-1">
        {targets.map((t) => {
          const changes = fields.filter((f) => {
            if (!SUMMARY_FIELDS.has(f)) return true;
            return !valueEquals(
              (source as unknown as Record<string, unknown>)[f],
              (t as unknown as Record<string, unknown>)[f],
            );
          });
          return (
            <li key={t.map_id}>
              <span className="font-mono">{t.map_id}</span>：
              {changes.length === 0 ? (
                <span className="text-[hsl(var(--muted-foreground))]">无变化</span>
              ) : (
                <span>{changes.join(", ")}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function valueEquals(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

/** 把 CopyPlan 转成多个 MapUpsertInput，调用方可以并发 PUT。 */
export function planToUpserts(plan: CopyPlan): MapUpsertInput[] {
  return plan.targets.map((t) => {
    const next: MapUpsertInput = {
      id: t.id,
      map_id: t.map_id,
      name: t.name,
      continent_id: t.continent_id,
      nested_group: t.nested_group,
      sort_order: t.sort_order,
      selectable_default: t.selectable_default,
      allow_transit_when_cooldown: t.allow_transit_when_cooldown,
      match: t.match,
      enter: t.enter,
      enterConditions: t.enterConditions,
      edges: t.edges,
      fight: t.fight,
      version: t.version,
    };
    for (const f of plan.fields) {
      // 直接拷贝源字段；用 unknown 中转避免 GameMap / MapUpsertInput 没有 index signature
      (next as unknown as Record<string, unknown>)[f] =
        (plan.source as unknown as Record<string, unknown>)[f];
    }
    return next;
  });
}
